import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Chip,
  Heading,
  InlineNotification,
  Modal,
  ModalControlModalProps,
  NakedUl,
  RadioCardField,
  Text,
  useAlert,
  useForm,
} from '@frontend/design-system';
import type { AvailabilityMode } from '../../../../../components/availability/availability-form/types';
import { AvailabilitySettingsTrackingIds } from '../../../../../trackingIds/availability-settings-tracking';
import { getAvailabilityRuleTypeOptions } from '../../helpers';

type Props = {
  currentMode: AvailabilityMode;
  existingRulesCount: number;
  locationId: string;
  modalProps: ModalControlModalProps;
};

export const ChangeAvailabilityModeModal = ({ currentMode, locationId, existingRulesCount, modalProps }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const alerts = useAlert();
  const tokens = useTranslationTokens();
  const options = getAvailabilityRuleTypeOptions(tokens);

  const updateLocationSettingsMutation = SchedulerV3.Mutations.useUpdateLocationSettingsMutation({
    options: {
      onSuccess: () => {
        modalProps.onClose();
      },
      onError: (error) => {
        console.error('Error updating availability mode:', error);
        alerts.error(t('Failed to update availability mode'));
      },
    },
  });

  const { getFieldProps, changedValues } = useForm({
    fields: {
      availabilityMode: {
        type: 'radio',
        value: currentMode,
      },
    },
    computeChangedValues: true,
  });

  const handleConfirmation = () => {
    if (!changedValues?.availabilityMode || changedValues.availabilityMode === currentMode) return;

    SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.availabilityModeConfirmChangeClicked, {
      locationId,
      previousAvailabilityMode: currentMode === 'OPERATORY' ? 'room' : 'provider',
      newAvailabilityMode: changedValues.availabilityMode === 'OPERATORY' ? 'room' : 'provider',
      existingRulesCount: existingRulesCount,
    });

    updateLocationSettingsMutation.mutate({
      locationId,
      availabilityLens: changedValues.availabilityMode,
    });
  };

  return (
    <Modal minWidth={600} maxWidth={600} {...modalProps}>
      <Modal.Header onClose={modalProps.onClose}>
        <Heading level={2}>{t('Change Availability Mode')}</Heading>
        <Text weight='regular'>
          {t(
            'Choose how you want to organize and calculate availability for scheduling. This setting determines how your schedules are structured.'
          )}
        </Text>
      </Modal.Header>
      <Modal.Body css={{ gap: theme.spacing(4) }}>
        <RadioCardField
          {...getFieldProps('availabilityMode')}
          css={{
            '> div': {
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: theme.spacing(2),
            },
          }}
        >
          {options.map((option) => (
            <RadioCardField.Option
              key={option.id}
              value={option.id}
              title={
                <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Text size='medium' weight='bold'>
                      {option.name}
                    </Text>
                    <Chip
                      size='tiny'
                      variant={option.iconVariant}
                      leftElement={<Icon name={option.icon} size={12} />}
                      title={option.iconTitle}
                    >
                      {option.iconTitle}
                    </Chip>
                  </div>
                  <Text size='small' weight='regular'>
                    {option.description}
                  </Text>
                </div>
              }
            />
          ))}
        </RadioCardField>
        <InlineNotification status='warn' css={{ maxHeight: '100%', '& > div': { width: '100%' } }}>
          <div css={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Text size='medium' weight='bold'>
              {t('This will affect existing schedules')}
            </Text>
            <Text size='medium'>{t('Changing modes will:')}</Text>
            <NakedUl css={{ listStyleType: 'initial', paddingLeft: theme.spacing(2.5) }}>
              <Text size='medium' as='li'>
                {t('Reorganize how availability is displayed and managed')}
              </Text>
              <Text size='medium' as='li'>
                {t('Require you to review and update schedule definitions')}
              </Text>
              <Text size='medium' as='li'>
                {t('Not affect existing booked appointments')}
              </Text>
            </NakedUl>
          </div>
        </InlineNotification>
      </Modal.Body>
      <Modal.Footer
        primary={{
          disabled: !changedValues?.availabilityMode,
          label: t('Confirm Change'),
          onClick: handleConfirmation,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
